import type { AppLocale } from '@/shared/lib';

import { env } from './env';

/** Locale that content is authored in and that search engines see as canonical. */
export const DEFAULT_LOCALE: AppLocale = 'uk';

/** Every locale the app can render, in menu order. */
export const SUPPORTED_LOCALES: readonly AppLocale[] = ['uk', 'ru', 'en'] as const;

/**
 * The locales a deployment serves, from a comma-separated setting.
 *
 * Kept in menu order, unknown codes ignored, and the canonical locale is
 * always present — a deployment with no Ukrainian would have no canonical
 * pages at all, so it cannot be configured away.
 */
export const parseEnabledLocales = (raw: string | undefined): readonly AppLocale[] => {
  const requested = new Set((raw ?? '').split(',').map((code) => code.trim()));
  return SUPPORTED_LOCALES.filter((locale) => locale === DEFAULT_LOCALE || requested.has(locale));
};

/**
 * What this build serves: `VITE_ENABLED_LOCALES`, `uk` at launch.
 *
 * The switcher, the prerender, the sitemap and the hreflang set all read this
 * list; SUPPORTED_LOCALES stays the set the code *can* render.
 */
export const ENABLED_LOCALES: readonly AppLocale[] = parseEnabledLocales(env.enabledLocales);

/** Human labels for the language switcher. */
export const LOCALE_LABELS: Record<AppLocale, string> = {
  uk: 'Українська',
  ru: 'Русский',
  en: 'English',
};

/** Compact switcher labels, each in its own language. */
export const LOCALE_SHORT_LABELS: Record<AppLocale, string> = { uk: 'Укр', ru: 'Рус', en: 'Eng' };

/**
 * URL prefix per locale. The language lives in the URL so every page has one
 * crawlable address per language (hreflang alternates) and the page loads
 * only that language; the canonical locale sits at the root.
 */
export const LOCALE_PREFIX: Record<AppLocale, string> = { uk: '', ru: '/ru', en: '/en' };

/** Locale and router basename for a full pathname ("/en/pricing" → en, "/en"). */
export const localeFromPath = (pathname: string): { locale: AppLocale; basename: string } => {
  const segment = `/${pathname.split('/')[1] ?? ''}`;
  const locale =
    SUPPORTED_LOCALES.find((item) => LOCALE_PREFIX[item] === segment) ?? DEFAULT_LOCALE;
  return { locale, basename: LOCALE_PREFIX[locale] };
};

/** The app path without its locale prefix ("/en/pricing" → "/pricing"). */
export const stripLocale = (pathname: string): string =>
  pathname.slice(localeFromPath(pathname).basename.length) || '/';

/** Public URL of an app path in a locale; locale homes end with a slash ("/en/"). */
export const localizedPath = (locale: AppLocale, path: string): string =>
  path === '/' ? `${LOCALE_PREFIX[locale]}/` : `${LOCALE_PREFIX[locale]}${path}`;

/**
 * Where a URL in a switched-off locale should go instead: the same page in
 * the canonical locale ("/ru/pricing" → "/pricing"), or null when the URL is
 * already served. The browser follows it with a replace; nginx is expected to
 * answer the same URLs with a 301 in production (DEPLOY_CHECKLIST).
 */
export const disabledLocaleRedirect = (
  pathname: string,
  enabled: readonly AppLocale[] = ENABLED_LOCALES,
): string | null => {
  const { locale } = localeFromPath(pathname);
  return enabled.includes(locale) ? null : localizedPath(DEFAULT_LOCALE, stripLocale(pathname));
};
